import React from 'react';
import memoize from 'lodash/memoize';
import capitalize from 'lodash/capitalize';
import Tooltip from '@atlaskit/tooltip';
import { Promotion, generatedTypesNew } from '@css-xp/graphtypes';
import { getPromotionCode } from '../../../helper';
import { OrderLineItem, Maybe, OrderBillTo } from '@css-xp/graphtypes/src/generated-types-new';
import { getDescriptor, getId, getOrderLineItemStatusAndType, getOrderType } from '../utils';
import {
  getCSVReadablePromotionTitle,
  getCSVReadablePromotionValue,
  getReadablePromotionTitle,
  getReadablePromotionValue,
} from '../page-tab-elements';
import './order-detail-view.scss';
import { BillingBehaviourType } from '@css-xp/ppc-utils';
import { TransitionTime } from '@atlassian/cssxp-typescommon';

export const getUnitsMessage = (quantity: OrderLineItem['quantity']) => {
  return quantity !== null ? ` - Tier/Qty: ${quantity}` : '';
};

const getPromotionDetailsMessage = (
  promotions: OrderLineItem['promotions'],
  isFlatDiscountSupportEnabled?: boolean
) => {
  return promotions?.map((promotion: Maybe<Promotion>, index: number) => {
    const promotionCode = getPromotionCode(promotion as generatedTypesNew.Promotion);
    return (
      <div key={index}>
        <p className="n-300">Promotion: {getReadablePromotionTitle(promotion)}</p>
        {promotionCode && <p className="n-300">Promo code: {promotionCode}</p>}
        <div className="n-300">
          Benefits: {getReadablePromotionValue(promotion, true, isFlatDiscountSupportEnabled)}
        </div>
      </div>
    );
  });
};

const getCSVPromotionDetailsMessage = (
  promotions: OrderLineItem['promotions'],
  isFlatDiscountSupportEnabled?: boolean
): string => {
  return (
    promotions
      ?.map((promotion: Maybe<Promotion>) => {
        const promotionCode = getPromotionCode(promotion as generatedTypesNew.Promotion);
        return `Promotion: ${getCSVReadablePromotionTitle(promotion)} ${
          promotionCode ? `Promo code: ${promotionCode}` : ''
        } Benefits: ${getCSVReadablePromotionValue(promotion, true, isFlatDiscountSupportEnabled)}`;
      })
      .join(' ') || ''
  );
};

const getOrderDetailContent = ({
  orderLineItem,
  billTo,
  isFlatDiscountSupportEnabled,
  legacyInvoiceId,
  isGracePeriodVisibilityEnabled,
}: {
  orderLineItem: Maybe<OrderLineItem>;
  billTo?: Maybe<OrderBillTo>;
  isFlatDiscountSupportEnabled?: boolean;
  legacyInvoiceId?: string;
  isGracePeriodVisibilityEnabled?: boolean;
}) => {
  const promotions = orderLineItem?.promotions;
  const quantity = orderLineItem?.quantity;
  const orderTransitionType = orderLineItem?.orderTransitionType;
  const salesTransitionType = orderLineItem?.salesTransitionType;
  const billToCompanyName = billTo?.billToCompanyName ?? '';
  const orderLineItemMessage = `
                      ${getOrderLineItemStatusAndType(
                        orderLineItem?.status,
                        orderLineItem?.type,
                        promotions,
                        orderLineItem?.orderLineItemsAccountModification
                      )}
                      ${getDescriptor(orderLineItem, orderLineItem?.productName as string, billToCompanyName)}
                      ${getId(orderLineItem)}${getUnitsMessage(quantity)}
                  `;
  const hasPromotions = promotions && promotions?.length;

  const getGracePeriodMessage = (lineItem: OrderLineItem) => {
    if (lineItem?.orderTransitionType === TransitionTime.SCHEDULED) {
      return 'Grace period scheduled';
    }
    if (lineItem?.orderLineBillingBehaviourObject?.startDate) {
      return 'Grace period applied';
    }
    return 'Grace period extended';
  };

  const orderDetailViewContent = (
    <>
      {orderLineItemMessage}
      {orderLineItem?.nextBillingAnchorTimestamp && (
        <>
          <p className="n-300">New bill date has been set</p>
        </>
      )}
      {hasPromotions && getPromotionDetailsMessage(promotions, isFlatDiscountSupportEnabled)}
      {(salesTransitionType || orderTransitionType) && (
        <div>
          {orderTransitionType && <span className="n-300">Transition: {capitalize(orderTransitionType)}</span>}{' '}
          {salesTransitionType && salesTransitionType !== 'NA' && (
            <span className="n-300">| {capitalize(salesTransitionType)}</span>
          )}
        </div>
      )}
      {legacyInvoiceId && <div>Legacy invoice id: {legacyInvoiceId}</div>}
      {isGracePeriodVisibilityEnabled &&
        orderLineItem?.orderLineBillingBehaviourObject?.type === BillingBehaviourType.PAUSE_BILLING && (
          <p>
            <span className="n-300">{getGracePeriodMessage(orderLineItem)}</span>
          </p>
        )}
    </>
  );

  return orderDetailViewContent;
};

export const OrderDetailView = memoize(
  ({
    orderLineItem,
    billTo,
    isFlatDiscountSupportEnabled = false,
    legacyInvoiceId,
    isGracePeriodVisibilityEnabled,
  }: {
    orderLineItem: Maybe<OrderLineItem>;
    billTo?: Maybe<OrderBillTo>;
    isFlatDiscountSupportEnabled?: boolean;
    legacyInvoiceId?: string;
    isGracePeriodVisibilityEnabled?: boolean;
  }): React.ReactElement => {
    const orderDetailViewContent = getOrderDetailContent({
      orderLineItem,
      billTo,
      isFlatDiscountSupportEnabled,
      legacyInvoiceId,
      isGracePeriodVisibilityEnabled,
    });
    const tooltipContent = <div className="order-detail-view tooltip">{orderDetailViewContent}</div>;

    return (
      <Tooltip position="bottom" content={tooltipContent}>
        <div className="order-detail-view line-clamp">{orderDetailViewContent}</div>
      </Tooltip>
    );
  }
);

export const OrderTypeView = memoize(
  ({
    orderLineItem,
    billTo,
    isFlatDiscountSupportEnabled = false,
    isGracePeriodVisibilityEnabled = false,
  }: {
    orderLineItem: Maybe<OrderLineItem>;
    billTo?: Maybe<OrderBillTo>;
    isFlatDiscountSupportEnabled?: boolean;
    isGracePeriodVisibilityEnabled?: boolean;
  }): React.ReactElement => {
    const orderTransitionType = orderLineItem?.orderTransitionType;
    const orderDetailViewContent = getOrderDetailContent({
      orderLineItem,
      billTo,
      isFlatDiscountSupportEnabled,
      isGracePeriodVisibilityEnabled,
    });
    const tooltipContent = <div className="order-detail-view tooltip">{orderDetailViewContent}</div>;
    const orderType = getOrderType(orderLineItem?.status, orderLineItem?.type);
    const isPurchaseOrder = orderType.isPurchaseOrder;

    return (
      <Tooltip position="bottom" content={tooltipContent}>
        <div className="order-type-view">
          {isPurchaseOrder ? (
            capitalize(orderType.text)
          ) : (
            <>
              {orderTransitionType && <span>{capitalize(orderTransitionType)} </span>}
              {orderTransitionType ? orderType.text : capitalize(orderType.text)}
            </>
          )}
        </div>
      </Tooltip>
    );
  }
);

export const getOrderDetailViewCSVContent = ({
  orderLineItem,
  billTo,
  isFlatDiscountSupportEnabled = false,
}: {
  orderLineItem: Maybe<OrderLineItem>;
  billTo?: Maybe<OrderBillTo>;
  isFlatDiscountSupportEnabled?: boolean;
}): string => {
  if (Object.keys(orderLineItem || {}).length === 0) {
    return '-';
  }
  const promotions = orderLineItem?.promotions;
  const quantity = orderLineItem?.quantity;
  const orderTransitionType = orderLineItem?.orderTransitionType;
  const salesTransitionType = orderLineItem?.salesTransitionType;
  const billToCompanyName = billTo?.billToCompanyName ?? '';
  const orderLineItemMessage = `${getOrderLineItemStatusAndType(
    orderLineItem?.status,
    orderLineItem?.type,
    promotions,
    orderLineItem?.orderLineItemsAccountModification
  )}${orderLineItem?.parentEntitlementId ? ' add-on ' : ''}${getDescriptor(
    orderLineItem,
    undefined,
    billToCompanyName
  )}${getId(orderLineItem)}${getUnitsMessage(quantity)}`;
  const hasPromotions = promotions && promotions?.length;

  return `${orderLineItemMessage}${orderLineItem?.nextBillingAnchorTimestamp ? ' New bill date has been set \n' : ''}${
    hasPromotions ? getCSVPromotionDetailsMessage(promotions, isFlatDiscountSupportEnabled) : ''
  }${
    salesTransitionType || orderTransitionType
      ? `${orderTransitionType ? ' Transition: ' + capitalize(orderTransitionType) : ''} ${
          salesTransitionType && salesTransitionType !== 'NA' ? ' | ' + capitalize(salesTransitionType) : ''
        }`
      : ''
  }`;
};
